import React, { memo, useEffect, useState } from 'react';
import { motion, animate, useReducedMotion, useMotionValue } from 'framer-motion';

interface MerchantCountBadgeProps {
  count: number;
}

export const MerchantCountBadge = memo(
  ({ count }: MerchantCountBadgeProps) => {
    const [displayCount, setDisplayCount] = useState(count);
    const countMotion = useMotionValue(count);
    
    // Respect accessibility settings
    const shouldReduceMotion = useReducedMotion();

    // Smooth counter animation
    useEffect(() => {
      if (shouldReduceMotion) {
        setDisplayCount(count);
        return;
      }

      const controls = animate(countMotion, count, {
        duration: 0.8,
        ease: 'easeOut',
        onUpdate(value) {
          setDisplayCount(Math.round(value));
        }
      });

      return () => controls.stop();
    }, [count, shouldReduceMotion, countMotion]);

    return (
      <motion.div
        initial={{
          opacity: 0,
          y: 12,
          scale: 0.96
        }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }}
        whileHover={
          typeof window !== 'undefined' && window.innerWidth >= 768
            ? { scale: 1.02 }
            : {}
        }
        whileTap={{
          scale: 0.98
        }}
        transition={{
          type: 'spring',
          stiffness: 260,
          damping: 22
        }}
        role="status"
        aria-label={`Total merchants ${count}`}
        className="
          flex items-center justify-center sm:justify-start
          gap-2 sm:gap-3
          w-full sm:w-auto
          rounded-2xl
          border border-[#EF4421]/30
          bg-[#EF4421]/10
          backdrop-blur-lg
          px-3 py-2
          sm:px-4 sm:py-3
          md:px-5
          shadow-md
          hover:shadow-lg
          transition-all duration-300
        "
      >
        {/* Icon */}
        <motion.div
          animate={
            shouldReduceMotion
              ? {}
              : {
                  rotate: [0, -5, 5, 0]
                }
          }
          transition={{
            duration: 1.6,
            repeat: Infinity,
            repeatDelay: 3
          }}
          className="
            flex items-center justify-center
            flex-shrink-0
            w-8 h-8
            sm:w-9 sm:h-9
            md:w-10 md:h-10
            rounded-xl
            bg-[#EF4421]
            shadow-sm
          "
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            aria-hidden="true"
            className="
              w-4 h-4
              sm:w-5 sm:h-5
              text-white
            "
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
        </motion.div>

        {/* Content */}
        <div className="flex items-baseline gap-1 sm:gap-2">
          <motion.span
            className="
              font-extrabold
              leading-none
              tabular-nums
              text-lg
              sm:text-xl
              md:text-2xl
              text-[#EF4421]
              transform-gpu
            "
          >
            {displayCount.toLocaleString()}
          </motion.span>

          <span
            className="
              font-semibold
              whitespace-nowrap
              text-xs
              sm:text-sm
              md:text-base
              text-[#D63A1C]
            "
          >
            Merchants
          </span>
        </div>
      </motion.div>
    );
  }
);

MerchantCountBadge.displayName = 'MerchantCountBadge';
